import type { CommonCart } from "../model/CommonCartTypes";
import type { ICartStore } from "./common-cart/CommonCartStore";
import type { PriceConfiguration } from "./BaseTypes";
import type { IPriceConfigStore } from "./PriceConfigStore";
import CountryUtil from "../uidispatcherapi/CountryUtil";
import { CustomerDataStoreImpl } from "./CustomerDataStore";
import { LazyStore } from "../utils/Store";
import { SideCaddyActions } from "../actions/SideCaddyActions";
import { CustomerCartActions } from "../actions/CustomerCartActions";
import { CountryStoresStoreImpl } from "./checkout/CountryStoresStore";
import { SelectedStoreInfoStoreImpl } from "./checkout/SelectedStoreInfoStore";
const MAX_CADDY_ITEMS = 5; //the most articles to render in the side caddy

const RECOMMENDATIONS_THRESHOLD = 2; //number of articles in cart for which recommendations are still rendered

export type SideCaddyStoreState = InternalSideCaddyStoreState & {
	cart: CommonCart | null | undefined;
	maxCaddyItems: number;
	recommendationsThreshold: number;
	maxQuantityNormal: number;
	maxQuantityWeight: number;
	priceConfiguration: PriceConfiguration;
	storeName: string | null | undefined;
};
export type InternalSideCaddyStoreState = {
	redirectToCart: boolean;
	isEnabled: boolean;
};
export class SideCaddyStoreImpl extends LazyStore {
	_cartStore: ICartStore;
	_customerDataStore: CustomerDataStoreImpl;
	_selectedStoreInfoStore: SelectedStoreInfoStoreImpl;
	_countryStoresStore: CountryStoresStoreImpl;
	_priceConfigStore: IPriceConfigStore;
	_state: InternalSideCaddyStoreState;

	constructor(
		cartStore: ICartStore,
		customerDataStore: CustomerDataStoreImpl,
		selectedStoreInfoStore: SelectedStoreInfoStoreImpl,
		countryStoresStore: CountryStoresStoreImpl,
		priceConfigStore: IPriceConfigStore
	) {
		super();
		this._cartStore = cartStore;
		this._customerDataStore = customerDataStore;
		this._selectedStoreInfoStore = selectedStoreInfoStore;
		this._priceConfigStore = priceConfigStore;
		this._countryStoresStore = countryStoresStore;
		this._state = {
			redirectToCart: false,
			isEnabled: false
		};
	}

	onArm = () => {
		this._resetState();

		this.listenTo(this._cartStore, this.onCartStoreUpdate);
		this.listenTo(this._customerDataStore, this.onUpdate);
		this.listenTo(this._countryStoresStore, this.onUpdate);
		this.listenTo(SideCaddyActions.setItemQuantity, this._setItemQuantity);
		this.listenTo(SideCaddyActions.deleteItem, this._deleteItem);
		this.listenTo(this._selectedStoreInfoStore, () => this.trigger());
		this.listenTo(this._priceConfigStore, () => this.trigger());
	};
	onDisarm = () => {
		this._resetState();
	};
	onCartStoreUpdate = () => {
		this.trigger();
	};
	onUpdate = () => {
		const isEnabled = this.isSideCaddyEnabled(true);

		if (this._state.isEnabled !== isEnabled) {
			this._state = { ...this._state, isEnabled };
			this.trigger();
		}
	};

	_resetState(): void {
		this._state = {
			redirectToCart: false,
			isEnabled: this.isSideCaddyEnabled(true)
		};
	}

	getStoreName(): string | null | undefined {
		const stores = this._countryStoresStore.getState().stores;

		const cart = this._cartStore.getCart();

		const storeId = cart && cart.storeId;
		const storeInformation = stores && storeId && stores[storeId];
		return storeInformation && storeInformation.storeName;
	}

	getState(): SideCaddyStoreState {
		const maxQuantitySettings = this._selectedStoreInfoStore.getCurrentMaxQuantitySettings();

		return {
			...this._state,
			cart: this._cartStore.getCart(),
			maxCaddyItems: MAX_CADDY_ITEMS,
			maxQuantityNormal: maxQuantitySettings.maxQuantityNormal,
			maxQuantityWeight: maxQuantitySettings.maxQuantityWeight,
			recommendationsThreshold: RECOMMENDATIONS_THRESHOLD,
			priceConfiguration: this._priceConfigStore.getConfig(),
			storeName: this.getStoreName()
		};
	}

	_setItemQuantity(bundleId: string, quantity: number): void {
		CustomerCartActions.setItemQuantity(bundleId, quantity, "side-caddy");
	}

	_deleteItem(bundleId: string): void {
		CustomerCartActions.deleteItem(bundleId, "side-caddy");
	}

	isSideCaddyEnabled(shallTrack?: boolean): boolean {
		const { customerData } = this._customerDataStore.getState();

		const showSideCaddy =
			!!customerData &&
			(customerData.validForFsd || !CountryUtil.getOptionalConfig().hideMiniShoppingCartForNonFsd);

		if (shallTrack) {
			SideCaddyActions.trackEnabled(showSideCaddy);
		}

		return showSideCaddy;
	}
}
